/**
 * Compares two location objects to determine if they represent the same location
 * @param {Object|null|string} location1 - First location (can be GooglePlacesAutocomplete object, string, or null)
 * @param {Object|null|string} location2 - Second location (can be GooglePlacesAutocomplete object, string, or null)
 * @returns {boolean} - True if locations are the same, false otherwise
 */
export const isSameLocation = (location1, location2) => {
  // If either location is null/undefined, they're not the same
  if (!location1 || !location2) {
    return false
  }

  // Extract place_id from both locations (preferred comparison method)
  const getPlaceId = (location) => {
    if (typeof location === 'string') {
      return null
    }
    // Try different possible structures
    if (location.value?.place_id) {
      return location.value.place_id
    }
    if (location.place_id) {
      return location.place_id
    }
    if (location.value?.description) {
      // For temporary place_ids created from strings, we'll compare by description
      return null
    }
    return null
  }

  const placeId1 = getPlaceId(location1)
  const placeId2 = getPlaceId(location2)

  // If both have place_ids, compare them
  if (placeId1 && placeId2) {
    // Exclude temporary place_ids (those starting with "temp_")
    if (!placeId1.startsWith('temp_') && !placeId2.startsWith('temp_')) {
      return placeId1 === placeId2
    }
  }

  // Fallback: Compare normalized address strings
  const normalizeAddress = (location) => {
    let address = ''
    
    if (typeof location === 'string') {
      address = location
    } else if (location.label) {
      address = location.label
    } else if (location.value?.description) {
      address = location.value.description
    }
    
    // Normalize: trim + lowercase
    return address.trim().toLowerCase()
  }

  const normalized1 = normalizeAddress(location1)
  const normalized2 = normalizeAddress(location2)

  // If either is empty after normalization, they're not the same
  if (!normalized1 || !normalized2) {
    return false
  }

  return normalized1 === normalized2
}

